import type {
  AddressProvider,
  DeliveryPointObject,
  HeatPumpModel,
  SiteReadModel,
  SiteResponse,
  SubscriberReadModel,
} from "@/.generated/hey/backoffice-backend/types.gen";
import type { TSiteAddress } from "@/domains/site/site-addresses.types";
import type { THeatPumpDeclarationDto } from "@/types/backoffice/dto/heatpump/heatpump-declaration-dto";
import type { THeatPumpModelDto } from "@/types/backoffice/dto/heatpump/heatpump-model-dto";
import type { TSitePdlDto } from "@/types/backoffice/dto/site/site-pdl-dto";
import type {
  CompanyId,
  ExternalSiteId,
  HeatPumpModelId,
  MissionId,
  OperationId,
  OwnerStatusId,
  SiteId,
  SiteLayoutId,
  SiteTypeId,
  SubscribedPowerId,
} from "@/types/common/ids";

import { enumValueOrRaw } from "@voltalis/frontend-utils";

import { mapSubscriber } from "@/api/backoffice-v2/subscriber/subscriber.mapping";
import { countryCodeValidator } from "@/domains/country/country.types";
import { ESiteAddressProvider } from "@/domains/site/site-addresses.types";
import { SiteModel, EApplianceEstimateCode } from "@/domains/site/site.types";
import { brandNumber, brandString } from "@/lib/branded";
import { EHeatPumpModelStatus } from "@/types/backoffice/dto/heatpump/heatpump-model-dto";
import { ESiteDeactivatedState, ESubscriberOffer, SiteBusinessSegment } from "@/types/backoffice/dto/site/site-dto";
import { EPdlStatus } from "@/types/backoffice/dto/site/site-pdl-dto";

function assertUnreachable(value: never): never {
  throw new Error(`Unexpected value: ${String(value)}`);
}

/**
 * Maps a raw response to a SiteModel
 */
export function mapSite(siteDto: SiteResponse): SiteModel {
  return {
    id: brandNumber<SiteId>(siteDto.id),
    externId: brandString<ExternalSiteId>(siteDto.externId),

    // New address
    siteAddress: extractAddress(siteDto),

    // Site properties
    businessSegment: enumValueOrRaw(SiteBusinessSegment, siteDto.businessSegment),
    surface: siteDto.surface,
    heatingSystemMoreThan2Years: siteDto.heatingSystemOlderThan2Years,
    buildingMoreThan2Years: siteDto.buildingOlderThan2Years,
    showPartnerBadge: siteDto.showPartnerBadge,
    siteTypeLayout: convertSiteTypeLayout(siteDto.layout),
    deactivatedState: siteDto.deactivatedState ? convertDeactivatedState(siteDto.deactivatedState) : null,
    wasEquippedWithTiko: siteDto.wasEquippedWithTiko ?? null,

    // Subscriber
    subscriber: siteDto.subscriber ? mapSubscriber(siteDto.subscriber) : null,
    subscriberCompany: convertSubscriberCompany(siteDto.subscriber?.company),
    subOwnerStatusType: convertSubOwnerStatusType(siteDto.subOwnerStatusType),
    subscribedPowerType: convertSubscribedPowerType(siteDto.subscribedPowerType),
    mandatoryInstallation: siteDto.mandatoryInstallation,
    subscriberOffer: siteDto.subscriberOffer ? convertSubscriberOffer(siteDto.subscriberOffer) : null,

    // DSO
    grd: convertGrd(siteDto.grd),
    deliveryPoint: siteDto.deliveryPoint?.code ?? null,
    pdl: siteDto.deliveryPoint ? convertDeliveryPoint(siteDto.deliveryPoint) : null,

    // Partnership
    mission: convertMission(siteDto.operation?.mission),
    operation: convertOperation(siteDto.operation),
    partner: siteDto.partner,

    // Phoning
    refMission: siteDto.phoningMission,

    // Appliances
    eligibleApplianceNumber: siteDto.eligibleApplianceNumber,
    applianceEstimates: siteDto.applianceEstimates?.map(convertApplianceEstimate) ?? [],
    heatPumpDeclarations: siteDto.heatPumpDeclarations.map(convertHeatPumpDeclaration),
  };
}

function convertApplianceEstimate(
  applianceEstimateDto: NonNullable<SiteResponse["applianceEstimates"]>[number],
): SiteModel["applianceEstimates"][number] {
  return {
    code: applianceEstimateDto.code,
    quantity: applianceEstimateDto.quantity,
  };
}

function convertSiteTypeLayout(layout: SiteResponse["layout"]): SiteModel["siteTypeLayout"] {
  if (!layout) {
    return null;
  }

  return {
    ...layout,
    type: {
      ...layout.type,
      id: brandNumber<SiteTypeId>(layout.type.id),
    },
    id: brandNumber<SiteLayoutId>(layout.id),
  };
}

function convertSubscriberCompany(company: SubscriberReadModel["company"] | undefined): SiteModel["subscriberCompany"] {
  if (!company) {
    return null;
  }

  return {
    ...company,
    id: brandNumber<CompanyId>(company.id),
  };
}

function convertSubOwnerStatusType(
  subOwnerStatusType: SiteResponse["subOwnerStatusType"],
): SiteModel["subOwnerStatusType"] {
  if (!subOwnerStatusType) {
    return null;
  }

  return {
    id: brandNumber<OwnerStatusId>(subOwnerStatusType.id),
    // The backend says description can be null, but in practice this ref never moves
    // and the description is never null (CONF_V3.REF_TYPE_SUB_OWNER_STATUS)
    description: subOwnerStatusType.description ?? "",
  };
}

function convertSubscribedPowerType(
  subscribedPowerType: SiteResponse["subscribedPowerType"],
): SiteModel["subscribedPowerType"] {
  return {
    ...subscribedPowerType,
    id: brandNumber<SubscribedPowerId>(subscribedPowerType.id),
  };
}

function convertGrd(grd: SiteResponse["grd"]): SiteModel["grd"] {
  if (!grd) {
    return null;
  }

  return {
    ...grd,
    id: brandNumber<CompanyId>(grd.id),
  };
}

function convertMission(mission: NonNullable<SiteResponse["operation"]>["mission"] | undefined): SiteModel["mission"] {
  if (!mission) {
    return null;
  }

  return {
    ...mission,
    id: brandNumber<MissionId>(mission.id),
  };
}

function convertOperation(operation: SiteResponse["operation"]): SiteModel["operation"] {
  if (!operation) {
    return null;
  }

  return {
    ...operation,
    partner: {
      ...operation.partner,
      id: brandNumber<CompanyId>(operation.partner.id),
    },
    id: brandNumber<OperationId>(operation.id),
  };
}

function convertHeatPumpDeclaration(
  declaration: SiteReadModel["heatPumpDeclarations"][number],
): THeatPumpDeclarationDto {
  return {
    id: brandNumber(declaration.id),
    creationDatetime: declaration.creationDatetime,
    model: convertHeatPumpModel(declaration.model),
    personId: brandNumber(declaration.personId),
    siteId: brandNumber(declaration.siteId),
  };
}

function convertHeatPumpModel(model: HeatPumpModel): THeatPumpModelDto {
  return {
    id: brandNumber<HeatPumpModelId>(model.id),
    model: model.model,
    modopFilePath: model.modopFilePath,
    status: convertHeatPumpStatus(model.status),
    brand: {
      ...model.brand,
      id: brandNumber(model.brand.id),
    },
  };
}

function convertHeatPumpStatus(status: HeatPumpModel["status"]): EHeatPumpModelStatus {
  switch (status) {
    case "NOT_VALIDATED":
      return EHeatPumpModelStatus.NOT_VALIDATED;
    case "VALIDATED_NOT_ELIGIBLE":
      return EHeatPumpModelStatus.VALIDATED_NOT_ELIGIBLE;
    case "VALIDATED_ELIGIBLE":
      return EHeatPumpModelStatus.VALIDATED_ELIGIBLE;
    default:
      return assertUnreachable(status);
  }
}

function convertSubscriberOffer(offer: NonNullable<SiteReadModel["subscriberOffer"]>): ESubscriberOffer {
  switch (offer) {
    case "BASIC":
      return ESubscriberOffer.BASIC;
    case "PREMIUM":
      return ESubscriberOffer.PREMIUM;
    case "PREMIUM_PENDING_SIGNATURE":
      return ESubscriberOffer.PREMIUM_PENDING_SIGNATURE;
    default:
      return assertUnreachable(offer);
  }
}

function convertDeliveryPoint(dp: NonNullable<SiteResponse["deliveryPoint"]>): TSitePdlDto {
  function toPdlStatus(status: DeliveryPointObject["status"]): EPdlStatus {
    switch (status) {
      case "VALID":
        return EPdlStatus.VALID;
      case "FORCED_VALID":
        return EPdlStatus.FORCED_VALID;
      case "PROCESSING":
        return EPdlStatus.PROCESSING;
      case "FORCED_INVALID":
        return EPdlStatus.FORCED_INVALID;
      case "INVALID":
        return EPdlStatus.INVALID;
      case "NOT_VERIFIED":
        return EPdlStatus.NOT_VERIFIED;
      case "NOT_ENEDIS":
        return EPdlStatus.NOT_ENEDIS;
      default:
        return assertUnreachable(status);
    }
  }

  return {
    pdl: dp.code,
    status: toPdlStatus(dp.status),
  };
}

function convertDeactivatedState(state: NonNullable<SiteReadModel["deactivatedState"]>): ESiteDeactivatedState {
  switch (state) {
    case "DEACTIVATED_WITH_MYV":
      return ESiteDeactivatedState.DEACTIVATED_WITH_MYV;
    case "DEACTIVATED_WITHOUT_MYV":
      return ESiteDeactivatedState.DEACTIVATED_WITHOUT_MYV;
    default:
      return assertUnreachable(state);
  }
}

function convertProvider(provider: AddressProvider): ESiteAddressProvider {
  switch (provider) {
    case "GOOGLE":
      return ESiteAddressProvider.GOOGLE;
    case "GOUVFR":
      return ESiteAddressProvider.GOUVFR;
    case "UNKNOWN":
      return ESiteAddressProvider.UNKNOWN;
    default:
      return assertUnreachable(provider);
  }
}

function extractAddress(siteResponse: SiteResponse): TSiteAddress {
  if (siteResponse.partialAddress) {
    return {
      type: "NON_GEOCODED",
      ...siteResponse.partialAddress,
      country: countryCodeValidator.parse(siteResponse.partialAddress.country),
      complement: siteResponse.partialAddress.complement ?? null,
      digicode: siteResponse.partialAddress.digicode ?? null,
      building: siteResponse.partialAddress.building ?? null,
      level: siteResponse.partialAddress.level ?? null,
      stairway: siteResponse.partialAddress.stairway ?? null,
    };
  }

  if (!siteResponse.address) {
    throw new Error("Unexpected site payload without address or partialAddress");
  }

  const country = countryCodeValidator.parse(siteResponse.address.country);

  switch (siteResponse.address.geocodingStatus) {
    case "GEOCODED":
      return {
        type: "GEOCODED",
        ...siteResponse.address,
        geocodedFields: {
          ...siteResponse.address.geocodedFields,
          provider: convertProvider(siteResponse.address.geocodedFields.provider),
        },
        country,
        complement: siteResponse.address.complement ?? null,
        digicode: siteResponse.address.digicode ?? null,
        building: siteResponse.address.building ?? null,
        level: siteResponse.address.level ?? null,
        stairway: siteResponse.address.stairway ?? null,
      };

    case "NEARBY":
      return {
        type: "NEARBY",
        ...siteResponse.address,
        nearbyFields: {
          ...siteResponse.address.nearbyFields,
          provider: convertProvider(siteResponse.address.nearbyFields.provider),
        },
        country,
        complement: siteResponse.address.complement ?? null,
        digicode: siteResponse.address.digicode ?? null,
        building: siteResponse.address.building ?? null,
        level: siteResponse.address.level ?? null,
        stairway: siteResponse.address.stairway ?? null,
      };
    default:
      return assertUnreachable(siteResponse.address);
  }
}

export function mapApplianceEstimatesAsRecord(
  applianceEstimates?: SiteModel["applianceEstimates"] | null,
): Partial<Record<EApplianceEstimateCode, number>> {
  if (!applianceEstimates) {
    return {};
  }

  return applianceEstimates.reduce(
    (prev, curr) => {
      return {
        ...prev,
        [curr.code]: curr.quantity,
      };
    },
    {} as Partial<Record<EApplianceEstimateCode, number>>,
  );
}

export function mapSiteApplianceEstimatesAsRecord(
  site?: (Pick<SiteModel, "eligibleApplianceNumber"> & Partial<Pick<SiteModel, "applianceEstimates">>) | null,
): Partial<Record<EApplianceEstimateCode, number>> {
  if (!site) {
    return {};
  }

  if (!site.applianceEstimates?.length) {
    return {
      [EApplianceEstimateCode.HEATER]: site.eligibleApplianceNumber ?? 0,
    };
  }

  return mapApplianceEstimatesAsRecord(site.applianceEstimates);
}

export function mapApplianceEstimatesFromRecord(
  applianceEstimates?: Partial<Record<EApplianceEstimateCode | string, number>> | null,
): SiteModel["applianceEstimates"] {
  if (!applianceEstimates) {
    return [];
  }

  // We want to update 3 declared codes but keep others as defined
  const {
    [EApplianceEstimateCode.HEAT_PUMP]: heatPumpDeclaration,
    [EApplianceEstimateCode.HEATER]: heaterDeclaration,
    [EApplianceEstimateCode.WATER_HEATER]: waterHeaterDeclaration,
    ...rest
  } = applianceEstimates;

  return [
    { code: EApplianceEstimateCode.HEAT_PUMP, quantity: heatPumpDeclaration ?? 0 },
    { code: EApplianceEstimateCode.HEATER, quantity: heaterDeclaration ?? 0 },
    { code: EApplianceEstimateCode.WATER_HEATER, quantity: waterHeaterDeclaration ?? 0 },
    ...Object.entries(rest).map(([k, v]) => ({ code: k, quantity: v ?? 0 })),
  ];
}
